import React, { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Loader2, PlugZap, CheckCircle2, XCircle, Lock } from "lucide-react";
import { toast } from "sonner";

const PLAIN = ["provider", "base_url", "auth_path", "einvoice_path", "ewaybill_path", "cancel_path", "status_path", "gstin", "mode"];
const SECRETS = ["client_id", "client_secret", "username", "password"];

export default function GstSettings() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [secrets, setSecrets] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  async function load() {
    try { const { data } = await api.get("/gst/config"); setCfg(data); setForm(data); }
    catch (e) { toast.error(apiError(e)); }
  }
  useEffect(() => { load(); }, []);

  function upd(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function save() {
    setSaving(true);
    try {
      const payload = {};
      PLAIN.forEach((k) => { payload[k] = form[k] || ""; });
      payload.enabled = !!form.enabled;
      SECRETS.forEach((k) => { if (secrets[k]) payload[k] = secrets[k]; });
      const { data } = await api.put("/gst/config", payload);
      setCfg(data); setForm(data); setSecrets({});
      toast.success("GST settings saved securely");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  }

  async function test() {
    setTesting(true); setResult(null);
    try { const { data } = await api.post("/gst/test-connection"); setResult(data); }
    catch (e) { setResult({ ok: false, message: apiError(e) }); } finally { setTesting(false); }
  }

  if (!cfg) return <div className="p-10 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="gst-settings-page">
      <div>
        <h2 className="font-display text-2xl font-bold text-[#0F172A] flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-[#1A2A5E]" /> GST API Settings</h2>
        <p className="text-slate-500 text-sm">Provider-agnostic e-Invoice & e-Way Bill. Credentials are encrypted and stored server-side only.</p>
      </div>

      <Card className="p-5 border-slate-200 space-y-4">
        <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-700">Enable GST Integration</span>
            {cfg.enabled ? <span className="text-xs text-emerald-600 font-semibold">Active</span> : <span className="text-xs text-slate-400">Disabled</span>}
          </div>
          <Switch checked={!!form.enabled} onCheckedChange={(v) => upd("enabled", v)} data-testid="gst-enabled-toggle" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Provider Name</Label><Input value={form.provider || ""} onChange={(e) => upd("provider", e.target.value)} placeholder="e.g. ClearTax / Masters India / NIC" className="mt-1" data-testid="gst-provider" /></div>
          <div><Label>Mode</Label>
            <Select value={form.mode || "sandbox"} onValueChange={(v) => upd("mode", v)}>
              <SelectTrigger className="mt-1" data-testid="gst-mode"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white/95 backdrop-blur-md"><SelectItem value="sandbox">Sandbox</SelectItem><SelectItem value="production">Production</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label>API Base URL</Label><Input value={form.base_url || ""} onChange={(e) => upd("base_url", e.target.value)} placeholder="https://api.provider.com" className="mt-1" data-testid="gst-base-url" /></div>
          <div><Label>Your GSTIN</Label><Input value={form.gstin || ""} onChange={(e) => upd("gstin", e.target.value)} className="mt-1" data-testid="gst-gstin" /></div>
          <div><Label>Auth Path</Label><Input value={form.auth_path || ""} onChange={(e) => upd("auth_path", e.target.value)} placeholder="/auth" className="mt-1" /></div>
          <div><Label>e-Invoice Path</Label><Input value={form.einvoice_path || ""} onChange={(e) => upd("einvoice_path", e.target.value)} placeholder="/einvoice/generate" className="mt-1" /></div>
          <div><Label>e-Way Bill Path</Label><Input value={form.ewaybill_path || ""} onChange={(e) => upd("ewaybill_path", e.target.value)} placeholder="/ewaybill/generate" className="mt-1" /></div>
          <div><Label>Cancel Path</Label><Input value={form.cancel_path || ""} onChange={(e) => upd("cancel_path", e.target.value)} placeholder="/einvoice/cancel" className="mt-1" /></div>
        </div>

        <div className="border-t pt-4">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 mb-3"><Lock className="h-4 w-4 text-[#F4571E]" /> Credentials (encrypted)</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {SECRETS.map((k) => (
              <div key={k}>
                <Label className="capitalize">{k.replace("_", " ")}{cfg[k + "_set"] && <span className="text-[10px] text-emerald-600 ml-2">saved</span>}</Label>
                <Input type={k.includes("secret") || k === "password" ? "password" : "text"} value={secrets[k] || ""} onChange={(e) => setSecrets((s) => ({ ...s, [k]: e.target.value }))} placeholder={cfg[k + "_set"] ? "•••••••• (leave blank to keep)" : "Enter value"} className="mt-1" data-testid={`gst-${k}`} />
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <Button onClick={save} disabled={saving} className="bg-[#1A2A5E]" data-testid="gst-save">{saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : "Save Settings"}</Button>
          <Button onClick={test} disabled={testing} variant="outline" data-testid="gst-test"><PlugZap className="h-4 w-4 mr-2" />{testing ? "Testing..." : "Test Connection"}</Button>
        </div>

        {result && (
          <div data-testid="gst-test-result" className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 ${result.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
            {result.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />} {result.message}
          </div>
        )}
      </Card>
    </div>
  );
}
